/**
 * Universal flow-node renderer.
 *
 * Each FlowDefinition node type gets a thin wrapper below that selects the
 * right icon, category color, and handle configuration. This keeps the
 * n8n-style visual system consistent across all nodes with minimal
 * duplication — matching FLOWS.md § 11.10.
 */

import type { NodeProps } from "@xyflow/react";
import { Handle, Position } from "@xyflow/react";
import { useEditorStore } from "../editorStore";
import {
  Zap,
  GitBranch,
  Split,
  FlaskConical,
  Clock,
  ShoppingCart,
  Lock,
  Timer,
  Shield,
  Webhook,
  type LucideIcon,
} from "lucide-react";
import { clsx } from "clsx";
import type { CanvasNode } from "../editorStore";
import { CardShell, type CategoryKey } from "./CardShell";
import type {
  ABTestNode,
  CheckoutPageNode,
  ConditionNode,
  DelayNode,
  FlowNode as FlowNodePayload,
  FraudCheckNode,
  HttpRequestNode,
  ScheduleNode,
  SwitchNode,
} from "../../../types/flow";

// ─── Handle output descriptors ───────────────────────────────────────────────

interface OutputHandle {
  id: string;
  label: string;
  // Horizontal offset in percent (Position.Bottom layout — handles on bottom edge)
  xPct: number;
  // Visual style
  color?: "true" | "false" | "neutral" | "error";
  hidden?: boolean;
}

function handleColorClass(color?: OutputHandle["color"]): string {
  switch (color) {
    case "true":
      return "!bg-category-trigger"; // green — rule matched
    case "false":
      return "!bg-neutral-500"; // slate — rule did not match (not an error)
    case "error":
      return "!bg-category-error"; // red — node failed at runtime
    default:
      return "!bg-neutral-400";
  }
}

// ─── Per-type config ─────────────────────────────────────────────────────────

interface NodeTypeConfig {
  icon: LucideIcon;
  category: CategoryKey;
  outputs: (payload: FlowNodePayload) => OutputHandle[];
  // hasInput: every non-trigger node has an input handle on top
  hasInput: boolean;
}

/**
 * Spread N handles evenly across the bottom edge with consistent margins.
 * Returns the xPct value for the i-th handle out of n.
 */
function spread(i: number, n: number): number {
  if (n === 1) return 50;
  const margin = 15; // % from each edge
  const usable = 100 - margin * 2;
  return margin + (i * usable) / (n - 1);
}

const CONFIG: Record<FlowNodePayload["type"], NodeTypeConfig> = {
  trigger: {
    icon: Zap,
    category: "trigger",
    hasInput: false,
    outputs: () => [{ id: "next", label: "next", xPct: 50 }],
  },
  condition: {
    icon: GitBranch,
    category: "routing",
    hasInput: true,
    outputs: (payload) => {
      const c = payload as ConditionNode;
      const has = c.error_next !== undefined;
      const outs: OutputHandle[] = [
        { id: "true_next", label: "true", xPct: has ? spread(0, 3) : 35, color: "true" },
        { id: "false_next", label: "false", xPct: has ? spread(1, 3) : 65, color: "false" },
      ];
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(2, 3), color: "error" });
      }
      return outs;
    },
  },
  switch: {
    icon: Split,
    category: "routing",
    hasInput: true,
    outputs: (payload) => {
      const s = payload as SwitchNode;
      const has = s.error_next !== undefined;
      const total = s.branches.length + 1 + (has ? 1 : 0);
      const outs: OutputHandle[] = s.branches.map((b, i) => ({
        id: `branch-${i}`,
        label: String(b.value),
        xPct: spread(i, total),
      }));
      outs.push({ id: "default_next", label: "default", xPct: spread(s.branches.length, total) });
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(total - 1, total), color: "error" });
      }
      return outs;
    },
  },
  ab_test: {
    icon: FlaskConical,
    category: "routing",
    hasInput: true,
    outputs: (payload) => {
      const a = payload as ABTestNode;
      const has = a.error_next !== undefined;
      const total = a.variants.length + (has ? 1 : 0);
      const outs: OutputHandle[] = a.variants.map((v, i) => ({
        id: `variant-${v.id}`,
        label: `${v.id} · ${v.weight}%`,
        xPct: spread(i, total),
      }));
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(total - 1, total), color: "error" });
      }
      return outs;
    },
  },
  schedule: {
    icon: Clock,
    category: "routing",
    hasInput: true,
    outputs: (payload) => {
      const s = payload as ScheduleNode;
      const has = s.error_next !== undefined;
      const outs: OutputHandle[] = [
        { id: "active_next", label: "active", xPct: has ? spread(0, 3) : 35, color: "true" },
        { id: "inactive_next", label: "inactive", xPct: has ? spread(1, 3) : 65 },
      ];
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(2, 3), color: "error" });
      }
      return outs;
    },
  },
  checkout_page: {
    icon: ShoppingCart,
    category: "terminal",
    hasInput: true,
    outputs: () => [{ id: "next", label: "post-checkout", xPct: 50 }],
  },
  capture_policy: {
    icon: Lock,
    category: "postpurchase",
    hasInput: true,
    outputs: () => [],
  },
  delay: {
    icon: Timer,
    category: "postpurchase",
    hasInput: true,
    outputs: (payload) => {
      const d = payload as DelayNode;
      const has = d.error_next !== undefined;
      const outs: OutputHandle[] = [
        { id: "next", label: "next", xPct: has ? spread(0, 2) : 50 },
      ];
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(1, 2), color: "error" });
      }
      return outs;
    },
  },
  fraud_check: {
    icon: Shield,
    category: "postpurchase",
    hasInput: true,
    outputs: (payload) => {
      const f = payload as FraudCheckNode;
      const has = f.error_next !== undefined;
      const total = 3 + (has ? 1 : 0);
      const outs: OutputHandle[] = [
        { id: "low_next", label: "low", xPct: spread(0, total), color: "true" },
        { id: "medium_next", label: "medium", xPct: spread(1, total) },
        { id: "high_next", label: "high", xPct: spread(2, total), color: "false" },
      ];
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(3, total), color: "error" });
      }
      return outs;
    },
  },
  http_request: {
    icon: Webhook,
    category: "action",
    hasInput: true,
    outputs: (payload) => {
      const h = payload as HttpRequestNode;
      const has = h.error_next !== undefined;
      const outs: OutputHandle[] = [
        { id: "next", label: "next", xPct: has ? spread(0, 2) : 50, color: "true" },
      ];
      if (has) {
        outs.push({ id: "error_next", label: "error", xPct: spread(1, 2), color: "error" });
      }
      return outs;
    },
  },
};

// ─── Summary (per-node card subtitle) ────────────────────────────────────────

function summaryFor(payload: FlowNodePayload): string {
  switch (payload.type) {
    case "trigger":
      return payload.sources
        .map((s) => (s.reference ? `${s.type}:${s.reference}` : s.type))
        .join(", ") || "no sources";
    case "condition":
      return "JSONLogic rule";
    case "switch":
      return `${(payload as SwitchNode).branches.length} branches + default`;
    case "ab_test": {
      const a = payload as ABTestNode;
      return a.variants.map((v) => `${v.id}:${v.weight}`).join(" / ");
    }
    case "schedule": {
      const s = payload as ScheduleNode;
      return `${s.window.start.slice(0, 10)} → ${s.window.end.slice(0, 10)}`;
    }
    case "checkout_page": {
      const p = payload as CheckoutPageNode;
      return `${p.layout} · ${p.payment_methods.length} methods`;
    }
    case "capture_policy":
      return `mode: ${payload.mode}`;
    case "delay":
      return payload.duration.mode === "fixed"
        ? `fixed ${payload.duration.iso8601}`
        : "until (JSONLogic)";
    case "fraud_check":
      return `${payload.provider} · 3 bands`;
    case "http_request": {
      const h = payload as HttpRequestNode;
      const url = h.url || "(no URL)";
      return `${h.method} ${url.length > 28 ? url.slice(0, 28) + "…" : url}`;
    }
  }
}

function defaultTitleFor(payload: FlowNodePayload): string {
  switch (payload.type) {
    case "trigger":
      return "Trigger";
    case "condition":
      return "Condition";
    case "switch":
      return "Switch";
    case "ab_test":
      return "A/B test";
    case "schedule":
      return "Schedule";
    case "checkout_page":
      return "Checkout page";
    case "capture_policy":
      return "Capture policy";
    case "delay":
      return "Delay";
    case "fraud_check":
      return "Fraud check";
    case "http_request":
      return "HTTP request";
  }
}

function titleFor(payload: FlowNodePayload): string {
  const label = (payload as { label?: string }).label;
  if (label && label.trim().length > 0) return label;
  return defaultTitleFor(payload);
}

// ─── Component ───────────────────────────────────────────────────────────────

function handleTextClass(color?: OutputHandle["color"]): string {
  switch (color) {
    case "true":
      return "text-category-trigger";
    case "error":
      return "text-category-error";
    case "false":
    default:
      return "text-neutral-500";
  }
}

export function FlowNodeComponent({ id, data, selected }: NodeProps<CanvasNode>) {
  const payload = data.payload;
  const config = CONFIG[payload.type];
  const outputs = config.outputs(payload);
  const deleteNode = useEditorStore((s) => s.deleteNode);
  const openAddMenu = useEditorStore((s) => s.openAddMenu);
  const edges = useEditorStore((s) => s.edges);
  // Trigger is the flow's start; not user-deletable.
  const canDelete = payload.type !== "trigger";

  // Per-output: is this handle already wired up? If yes, we hide its "+"
  // badge AND disable drag-from-this-handle so the user has to delete the
  // existing edge before connecting elsewhere — guarantees one-out-per-handle.
  const handleIsConnected = (handleId: string) =>
    edges.some((e) => e.source === id && e.sourceHandle === handleId);

  return (
    <>
      {config.hasInput ? (
        <Handle
          type="target"
          position={Position.Top}
          className="!bg-neutral-400 !w-2 !h-2"
        />
      ) : null}

      <CardShell
        category={config.category}
        icon={config.icon}
        title={titleFor(payload)}
        summary={summaryFor(payload)}
        selected={selected}
        onDelete={canDelete ? () => deleteNode(id) : undefined}
      />

      {outputs.map((h) => {
        const connected = handleIsConnected(h.id);
        return (
          <div key={h.id}>
            <Handle
              type="source"
              position={Position.Bottom}
              id={h.id}
              isConnectableStart={!connected}
              style={{ left: `${h.xPct}%` }}
              className={clsx(
                "!w-2.5 !h-2.5",
                handleColorClass(h.color),
                // Visual cue: a connected handle is solid + slightly larger so
                // the user reads it as "owned" rather than "available".
                connected && "ring-2 ring-white dark:ring-neutral-900",
              )}
              title={connected ? "Already connected — delete the line first to rewire" : undefined}
            />
            <span
              className={clsx(
                "absolute text-[9px] font-medium whitespace-nowrap pointer-events-none select-none",
                "px-1 rounded bg-white/85 dark:bg-neutral-900/85",
                handleTextClass(h.color),
              )}
              style={{
                left: `${h.xPct}%`,
                bottom: "-18px",
                transform: "translateX(-50%)",
              }}
            >
              {h.label}
            </span>
            {!connected ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  openAddMenu(rect.left + rect.width / 2, rect.bottom + 4, {
                    sourceId: id,
                    sourceHandle: h.id,
                  });
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className={clsx(
                  "cp-add-handle-btn nodrag",
                  "absolute flex items-center justify-center w-4 h-4 rounded-full",
                  "border border-dashed border-neutral-400 dark:border-neutral-600",
                  "text-neutral-500 hover:text-white hover:bg-neutral-700 hover:border-neutral-700",
                  "bg-white dark:bg-neutral-900 transition-opacity",
                )}
                style={{
                  left: `${h.xPct}%`,
                  bottom: "-38px",
                  transform: "translateX(-50%)",
                }}
                aria-label={`Add a node after ${h.label}`}
                title="Add next node"
              >
                <span className="text-[12px] leading-none -mt-0.5">+</span>
              </button>
            ) : null}
          </div>
        );
      })}

    </>
  );
}

